import React from 'react';
import { ArrowRightIcon, DownloadIcon, InfinityIcon } from 'lucide-react';
export function HeroSection() {
  return <section className="relative w-full bg-gradient-to-b from-lavender-light to-cream overflow-hidden" data-id="element-143">
      {/* ===== ABOVE THE FOLD: Hero with image + CTA ===== */}
      <div className="px-5 md:px-8 lg:px-12 md:pt-12 md:pb-16 pt-[30px] pb-[30px]" data-id="element-144">
        <div className="max-w-5xl mx-auto" data-id="element-145">
          {/* Two-column on desktop, stacked on mobile */}
          <div className="flex flex-col md:flex-row md:items-center md:gap-12 lg:gap-16" data-id="element-146">
            {/* LEFT COLUMN: Headline + Hook + CTA */}
            <div className="flex-1 text-center md:text-left" data-id="element-147">
              {/* H1 */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-text mb-4" data-id="element-148">
                El posparto no se 'supera'.
                <br data-id="element-149" />
                <span className="italic text-lavender-dark" data-id="element-150">
                  Se sobrevive con un guión.
                </span>
              </h1>

              {/* H2 Subheadline */}
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-text/90 leading-snug mb-5" data-id="element-151">
                Tu guión diario para el primer mes con bebé: sin improvisar, sin
                culpa y sin necesitar ayuda de nadie.
              </h2>

              {/* Supporting copy — 2 lines */}
              <div className="text-base md:text-lg text-text/80 leading-relaxed mb-6 space-y-2 max-w-lg mx-auto md:mx-0" data-id="element-152">
                <p className="text-text font-[400]" data-id="element-153">
                  Si hoy estás en modo zombie: abre el Día 1 y sigue el guión.
                  Solo eso.
                </p>
              </div>

              {/* Mockup image — MOBILE ONLY (shows right after copy) */}
              <div className="md:hidden relative w-full max-w-sm mx-auto mb-3 rounded-2xl overflow-hidden shadow-xl" data-id="element-154">
                <img src="https://cdn.magicpatterns.com/uploads/6U8PtuKcfmMad5JTsgkXLf/Mockup_mama_con_ipad_y_cel.png" alt="Mockup del Plan de Supervivencia Postparto en iPad y celular" className="w-full h-auto object-cover" data-id="element-155" />
              </div>

              {/* Text below image on mobile */}

              {/* Urgency badge */}
              <div className="flex justify-center md:justify-start mb-4" data-id="element-156"></div>

              {/* Price */}

              {/* CTA Button with glow */}
              <div className="flex flex-col items-center md:items-start gap-3" data-id="element-157">
                <a href="#offer-price" className="group inline-flex items-center gap-3 bg-lavender-dark hover:bg-text text-white px-8 py-4 rounded-full font-bold transition-all duration-300 animate-soft-glow hover:shadow-xl transform hover:-translate-y-1 ml-[5px] mr-[5px] text-[25px] pt-[25px] pb-[25px] pl-[10px] pr-[10px]" data-id="element-158">
                  Quiero mi guión de supervivencia → $6.36
                </a>

                {/* Tagline below button */}
                <p className="text-sm text-text-light text-center md:text-left font-[500]" data-id="element-159">
                  Menos que un café. Para el mes más difícil de tu vida.
                </p>

                {/* Trust signals */}
                <div className="flex items-center gap-4 text-sm text-text-light pt-[20px] pb-[20px]" data-id="element-160">
                  <span className="inline-flex items-center gap-1.5" data-id="element-161">
                    <DownloadIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-162" />
                    Descarga inmediata
                  </span>
                  <span className="inline-flex items-center gap-1.5" data-id="element-163">
                    <InfinityIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-164" />
                    Acceso de por vida
                  </span>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Mockup image — DESKTOP ONLY */}
            <div className="hidden md:block flex-1 relative" data-id="element-165">
              <div className="relative w-full max-w-md mx-auto rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500" data-id="element-166">
                <img src="https://cdn.magicpatterns.com/uploads/6U8PtuKcfmMad5JTsgkXLf/Mockup_mama_con_ipad_y_cel.png" alt="Mockup del Plan de Supervivencia Postparto en iPad y celular" className="w-full h-auto object-cover" data-id="element-167" />
              </div>
              {/* Decorative glow behind image */}
              <div className="absolute -inset-4 bg-lavender/20 rounded-3xl blur-3xl -z-10" aria-hidden="true" data-id="element-168" />
            </div>
          </div>
        </div>
      </div>

      {/* ===== VISUAL SEPARATOR ===== */}
      <div className="w-full h-px bg-gradient-to-r from-transparent via-lavender/30 to-transparent" data-id="element-169"></div>

      {/* ===== BELOW THE FOLD: Pain agitation block ===== */}
      <div className="px-5 py-12 md:px-8 lg:px-12 md:py-16 bg-cream pt-[50px] pb-[50px]" data-id="element-170">
        <div className="max-w-2xl mx-auto" data-id="element-171">
          <h2 className="text-2xl md:text-3xl font-bold text-text text-center mb-8" data-id="element-172">
            Nadie te dijo que el posparto se sentiría así...
          </h2>

          <ul className="space-y-4 mb-8" data-id="element-173">
            {['Te levantas sin saber qué hacer primero', 'Estás agotada, pero no puedes dormir', 'Sientes culpa si descansas, culpa si lloras', 'Ves el caos y no sabes por dónde empezar', 'Todo el mundo opina, nadie realmente ayuda', 'Y encima, se supone que debes "disfrutarlo"'].map((item, idx) => <li key={idx} className="flex items-start gap-3 text-lg text-text/80" data-id="element-174">
                <span className="text-xl flex-shrink-0" aria-hidden="true" data-id="element-175">
                  😶
                </span>
                <span className="text-[22px]" data-id="element-176">{item}</span>
              </li>)}
          </ul>

          <p className="md:text-2xl font-bold text-text text-center text-[24px]" data-id="element-177">
            No estás fallando. Estás en posparto sin guión. ¡Eso se soluciona!
          </p>
        </div>
      </div>
    </section>;
}